/**
 * The built-in node types. To make the app track something new, add an
 * entry here — nothing else in the codebase needs to know about it.
 *
 * `withingsMeasType` values come from the Withings Measure API `meastype`
 * table; the sync job derives what to request from whichever nodes a space
 * actually has, so it never fetches data nobody asked for.
 */
export const NODE_CATALOG = [
    {
        key: 'body.weight',
        kind: 'metric',
        title: 'Weight',
        description: 'Every weigh-in over time, joined exactly as measured.',
        icon: 'scale',
        keywords: ['mass', 'kilograms', 'kg', 'pounds', 'lbs', 'scale', 'bmi', 'slimming'],
        category: 'body',
        sources: ['withings', 'manual'],
        integrations: [
            { provider: 'withings', provides: 'Every weigh-in, with the moment it was taken.' },
        ],
        withingsMeasType: 1,
        metric: {
            unit: { canonical: 'kg', display: 'kg', precision: 1 },
            aggregation: 'mean',
            plausibleRange: { min: 20, max: 300 },
        },
    },
    {
        key: 'body.fat_ratio',
        kind: 'metric',
        title: 'Body fat',
        description: 'Fat percentage as reported by a smart scale.',
        icon: 'percent',
        keywords: ['fat', 'adiposity', 'body composition', 'lean', 'percentage', 'bioimpedance'],
        category: 'body',
        sources: ['withings', 'manual'],
        integrations: [
            { provider: 'withings', provides: 'The fat percentage a body-composition scale works out alongside your weight.' },
        ],
        withingsMeasType: 6,
        metric: {
            unit: { canonical: '%', display: '%', precision: 1 },
            aggregation: 'mean',
            plausibleRange: { min: 2, max: 70 },
        },
    },
    {
        key: 'body.muscle_mass',
        kind: 'metric',
        title: 'Muscle mass',
        description: 'Lean mass in kilograms, useful read alongside weight.',
        icon: 'dumbbell',
        keywords: ['muscle', 'lean mass', 'strength', 'body composition', 'skeletal'],
        category: 'body',
        sources: ['withings', 'manual'],
        integrations: [
            { provider: 'withings', provides: 'The lean mass figure from that same weigh-in.' },
        ],
        withingsMeasType: 76,
        metric: {
            unit: { canonical: 'kg', display: 'kg', precision: 1 },
            aggregation: 'mean',
            plausibleRange: { min: 10, max: 150 },
        },
    },
    {
        key: 'body.heart_rate',
        kind: 'metric',
        title: 'Heart rate',
        description: 'Pulse recorded at measurement time.',
        icon: 'heart-pulse',
        keywords: ['pulse', 'bpm', 'beats per minute', 'resting heart rate', 'cardio', 'cardiac'],
        category: 'body',
        sources: ['withings', 'manual'],
        integrations: [
            { provider: 'withings', provides: 'The pulse the scale or monitor records while it measures you.' },
        ],
        withingsMeasType: 11,
        metric: {
            unit: { canonical: 'bpm', display: 'bpm', precision: 0 },
            aggregation: 'mean',
            plausibleRange: { min: 25, max: 220 },
            suggestedAxis: { min: 40, max: 100 },
        },
    },
    {
        key: 'body.blood_pressure_systolic',
        kind: 'metric',
        title: 'Systolic blood pressure',
        description: 'The upper number from a blood pressure reading.',
        icon: 'activity',
        keywords: [
            'blood pressure',
            'hypertension',
            'upper number',
            'mmhg',
            'systole',
            'cardiovascular',
        ],
        category: 'body',
        sources: ['withings', 'manual'],
        integrations: [
            { provider: 'withings', provides: 'The upper number from each blood pressure reading.' },
        ],
        withingsMeasType: 10,
        metric: {
            unit: { canonical: 'mmHg', display: 'mmHg', precision: 0 },
            aggregation: 'mean',
            plausibleRange: { min: 60, max: 250 },
        },
    },
    {
        key: 'body.blood_pressure_diastolic',
        kind: 'metric',
        title: 'Diastolic blood pressure',
        description: 'The lower number from a blood pressure reading.',
        icon: 'activity',
        keywords: [
            'blood pressure',
            'hypertension',
            'lower number',
            'mmhg',
            'diastole',
            'cardiovascular',
        ],
        category: 'body',
        sources: ['withings', 'manual'],
        integrations: [
            { provider: 'withings', provides: 'The lower number from the same reading.' },
        ],
        withingsMeasType: 9,
        metric: {
            unit: { canonical: 'mmHg', display: 'mmHg', precision: 0 },
            aggregation: 'mean',
            plausibleRange: { min: 30, max: 150 },
        },
    },
    {
        key: 'food.library',
        kind: 'collection',
        title: 'Food library',
        description: 'The food you buy, with its nutrients per 100 g. Enter a product once, then find it by name.',
        icon: 'shopping-basket',
        keywords: [
            'food',
            'groceries',
            'shopping',
            'products',
            'pantry',
            'nutrition',
            'nutrients',
            'calories',
            'kcal',
            'protein',
            'barcode',
            'supermarket',
            'diet',
        ],
        category: 'body',
        sources: ['manual'],
        integrations: [
            {
                provider: 'openfoodfacts',
                provides: 'Scan a barcode and the name, photo and nutrients are filled in for you to check.',
            },
        ],
        holds: 'foods',
    },
    {
        key: 'custom.number',
        kind: 'metric',
        title: 'Custom number',
        description: 'Anything you want to count or measure yourself. You pick the unit.',
        icon: 'sigma',
        keywords: ['anything', 'count', 'tally', 'own unit', 'habit', 'streak', 'tracker'],
        category: 'other',
        sources: ['manual'],
        metric: {
            unit: { canonical: 'x', display: 'x', precision: 1 },
            aggregation: 'sum',
        },
    },
    {
        key: 'life.events',
        kind: 'collection',
        title: 'Significant life events',
        description: 'Moments and periods worth remembering — and the context behind a number that moved.',
        icon: 'milestone',
        keywords: [
            'life event',
            'milestone',
            'timeline',
            'holiday',
            'vacation',
            'bereavement',
            'loss',
            'illness',
            'injury',
            'moving house',
            'new job',
            'wedding',
            'birth',
            'context',
            'annotation',
            'memory',
            'period',
            'what happened',
        ],
        category: 'other',
        sources: ['manual'],
        overlay: 'timeline',
    },
    {
        key: 'heritage.family_tree',
        kind: 'collection',
        title: 'Family tree',
        description: 'People, relationships and sources from your genealogy research.',
        icon: 'git-fork',
        keywords: [
            'genealogy',
            'ancestry',
            'ancestors',
            'forebears',
            'descendants',
            'lineage',
            'pedigree',
            'relatives',
            'family history',
            'generations',
        ],
        category: 'heritage',
        sources: ['manual', 'import'],
    },
    {
        key: 'heritage.research_note',
        kind: 'document',
        title: 'Research note',
        description: 'A written piece with attachments — a family story, a source write-up.',
        icon: 'scroll-text',
        keywords: [
            'note',
            'writing',
            'essay',
            'story',
            'source',
            'archive',
            'transcription',
            'attachment',
            'genealogy',
        ],
        category: 'heritage',
        sources: ['manual', 'import'],
    },
];
const byKey = new Map(NODE_CATALOG.map((definition) => [definition.key, definition]));
export function findNodeType(key) {
    return byKey.get(key);
}
/**
 * Throws rather than returning undefined, for the many call sites where a
 * missing definition means the database holds a key we deleted from the
 * catalog — a bug worth surfacing loudly.
 */
export function requireNodeType(key) {
    const definition = byKey.get(key);
    if (!definition)
        throw new Error(`Unknown node type: ${key}`);
    return definition;
}
/** Every Withings meastype the catalog knows how to store, deduplicated. */
export function withingsMeasTypes(keys) {
    const types = new Set();
    for (const key of keys) {
        const measType = byKey.get(key)?.withingsMeasType;
        if (measType !== undefined)
            types.add(measType);
    }
    return [...types].sort((a, b) => a - b);
}
//# sourceMappingURL=catalog.js.map